import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TIER_KEYS, tierLabel } from '../../config/pricing';
import RefundModal from '../../components/RefundModal';
import ThemeToggle from '../../components/ThemeToggle';

function MasterAdminTenantDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === 'new';

  const [tenant, setTenant] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    subdomain: '',
    subscriptionTier: 'free_trial',
    subscriptionStatus: 'trial'
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [adminCredentials, setAdminCredentials] = useState(null);

  // Subdomain checking state
  const [subdomainStatus, setSubdomainStatus] = useState({ checking: false, available: null, reason: null });

  // Payment history and refund dialog.
  const [payments, setPayments] = useState([]);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundPreview, setRefundPreview] = useState(null);
  const [cancelTenant, setCancelTenant] = useState(false);

  const token = localStorage.getItem('masterAdminToken');
  const user = JSON.parse(localStorage.getItem('masterAdminUser') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/master-admin/login');
      return;
    }
    if (!isNew) {
      fetchTenant();
      fetchPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, id, isNew]);

  // Payment history for this tenant, and the refund dialog it drives.
  const fetchPayments = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/master-admin/payments?tenantId=${id}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) return;
      const data = await response.json();
      setPayments(data.payments || []);
    } catch {
      // Payment history is supplementary; a failure here must not blank the
      // tenant page the admin came to edit.
    }
  };

  const openRefund = async (payment) => {
    setRefundTarget(payment);
    setRefundPreview(null);
    setCancelTenant(false);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/master-admin/tenant-payments/${payment.id}/refund-preview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Failed to load refund details');
      setRefundPreview(await response.json());
    } catch (err) {
      setError(err.message);
      setRefundTarget(null);
    }
  };

  const submitRefund = async ({ amount, reason }) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/master-admin/tenant-payments/${refundTarget.id}/refund`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          amount,
          reason,
          setTenantStatus: cancelTenant ? 'cancelled' : null
        })
      }
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.details ? `${data.error}: ${data.details}` : data.error);
    }

    setRefundTarget(null);
    setRefundPreview(null);
    setSuccess(data.message + (data.tenantStatusChanged ? ' Tenant set to cancelled; recurring billing stopped.' : ''));
    await fetchTenant();
    await fetchPayments();
  };

  const fetchTenant = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('masterAdminToken');
        navigate('/master-admin/login');
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setTenant(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Debounced subdomain check
  const checkSubdomain = useCallback(async (subdomain) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainStatus({ checking: false, available: null, reason: 'Subdomain must be at least 3 characters' });
      return;
    }

    setSubdomainStatus({ checking: true, available: null, reason: null });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/check-subdomain/${subdomain}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSubdomainStatus({ checking: false, available: data.available, reason: data.reason });
    } catch (err) {
      setSubdomainStatus({ checking: false, available: null, reason: 'Error checking subdomain' });
    }
  }, [token]);

  // Debounce subdomain input
  useEffect(() => {
    if (!isNew || !tenant.subdomain) {
      setSubdomainStatus({ checking: false, available: null, reason: null });
      return;
    }

    const timer = setTimeout(() => {
      checkSubdomain(tenant.subdomain);
    }, 500);

    return () => clearTimeout(timer);
  }, [tenant.subdomain, isNew, checkSubdomain]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    setAdminCredentials(null);

    try {
      const url = isNew
        ? `${process.env.REACT_APP_API_URL}/master-admin/tenants`
        : `${process.env.REACT_APP_API_URL}/master-admin/tenants/${id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(tenant)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      if (isNew && data.adminCredentials) {
        setAdminCredentials(data.adminCredentials);
        setSuccess('Tenant created successfully! Admin credentials are shown below.');
        // Scroll to top to show success message and credentials
        window.scrollTo(0, 0);
      } else {
        setSuccess(isNew ? 'Tenant created successfully!' : 'Tenant updated successfully!');
        window.scrollTo(0, 0);
        if (isNew) {
          setTimeout(() => navigate(`/master-admin/tenants/${data.id}`), 1500);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${tenant.companyName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      navigate('/master-admin/tenants');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdminUser');
    navigate('/master-admin/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="admin-page master-admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page master-admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>DeedStack Platform Admin</h1>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-name">{user.firstName} {user.lastName}</span>
          <ThemeToggle />
          <button onClick={handleLogout} className="btn btn-secondary btn-small">
            Logout
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <Link to="/master-admin/dashboard" className="admin-nav-link">Dashboard</Link>
        <Link to="/master-admin/tenants" className="admin-nav-link active">Tenants</Link>
        <Link to="/master-admin/calendar" className="admin-nav-link">Calendar</Link>
        <Link to="/master-admin/emails" className="admin-nav-link">Emails</Link>
        <Link to="/master-admin/billing" className="admin-nav-link">Billing</Link>
        <Link to="/master-admin/financials" className="admin-nav-link">Financials</Link>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-container">
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>{isNew ? 'Add New Tenant' : `Edit: ${tenant.companyName}`}</h2>
              <Link to="/master-admin/tenants" className="btn btn-secondary">
                Back to Tenants
              </Link>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {/* Admin Credentials Card - shown after successful creation */}
            {adminCredentials && (
              <div className="admin-card" style={{ backgroundColor: 'var(--color-success-bg)', border: '2px solid var(--color-success-border)', marginBottom: '1.5rem' }}>
                <h3 style={{ color: 'var(--color-success-text)', marginBottom: '1rem' }}>✓ Tenant Admin Account Created</h3>
                <p style={{ marginBottom: '1rem' }}>{adminCredentials.message}</p>

                <div style={{ backgroundColor: 'var(--color-surface)', padding: '1rem', borderRadius: '4px', fontFamily: 'monospace' }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Email:</strong> {adminCredentials.email}
                    <button
                      type="button"
                      onClick={() => copyToClipboard(adminCredentials.email)}
                      style={{ marginLeft: '0.5rem', padding: '2px 8px', fontSize: '0.8em', cursor: 'pointer' }}
                    >
                      Copy
                    </button>
                  </div>
                  <div>
                    <strong>Temporary Password:</strong> {adminCredentials.tempPassword}
                    <button
                      type="button"
                      onClick={() => copyToClipboard(adminCredentials.tempPassword)}
                      style={{ marginLeft: '0.5rem', padding: '2px 8px', fontSize: '0.8em', cursor: 'pointer' }}
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <p style={{ marginTop: '1rem', fontSize: '0.9em', color: 'var(--color-text-light)' }}>
                  The tenant admin can log in at <strong>/admin/login</strong> with these credentials.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-card">
                <h3>Company Information</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="companyName">Company Name *</label>
                    <input
                      type="text"
                      id="companyName"
                      value={tenant.companyName}
                      onChange={(e) => setTenant({ ...tenant, companyName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subdomain">Subdomain</label>
                    <div className="subdomain-input-wrapper">
                      <input
                        type="text"
                        id="subdomain"
                        value={tenant.subdomain || ''}
                        onChange={(e) => setTenant({ ...tenant, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder="company"
                        className={subdomainStatus.available === true ? 'input-valid' : subdomainStatus.available === false ? 'input-invalid' : ''}
                      />
                      <span className="subdomain-suffix">.deedstack.com</span>
                      {isNew && tenant.subdomain && (
                        <span className="subdomain-status">
                          {subdomainStatus.checking && <span className="status-checking">⏳</span>}
                          {subdomainStatus.available === true && <span className="status-available">✓</span>}
                          {subdomainStatus.available === false && <span className="status-taken">✗</span>}
                        </span>
                      )}
                    </div>
                    {subdomainStatus.reason && (
                      <small className={subdomainStatus.available ? 'text-success' : 'text-danger'}>
                        {subdomainStatus.reason}
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="contactName">Contact Name</label>
                    <input
                      type="text"
                      id="contactName"
                      value={tenant.contactName || ''}
                      onChange={(e) => setTenant({ ...tenant, contactName: e.target.value })}
                      placeholder="First Last"
                    />
                    <small className="form-hint">Used to create admin account (first/last name)</small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactEmail">Contact Email *</label>
                    <input
                      type="email"
                      id="contactEmail"
                      value={tenant.contactEmail}
                      onChange={(e) => setTenant({ ...tenant, contactEmail: e.target.value })}
                      required
                    />
                    <small className="form-hint">Used as admin login email</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="contactPhone">Phone</label>
                    <input
                      type="tel"
                      id="contactPhone"
                      value={tenant.contactPhone || ''}
                      onChange={(e) => setTenant({ ...tenant, contactPhone: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="admin-card">
                <h3>Subscription</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subscriptionTier">Tier</label>
                    <select
                      id="subscriptionTier"
                      value={tenant.subscriptionTier}
                      onChange={(e) => setTenant({ ...tenant, subscriptionTier: e.target.value })}
                    >
                      {/* Prices from config/pricing.js -- never hardcoded here. */}
                      {TIER_KEYS.map(key => (
                        <option key={key} value={key}>{tierLabel(key)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="subscriptionStatus">Status</label>
                    <select
                      id="subscriptionStatus"
                      value={tenant.subscriptionStatus}
                      onChange={(e) => setTenant({ ...tenant, subscriptionStatus: e.target.value })}
                    >
                      <option value="trial">Trial</option>
                      <option value="active">Active</option>
                      <option value="past_due">Past Due</option>
                      <option value="suspended">Suspended</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                {/*
                  AUCTION CALENDAR ENTITLEMENT.

                  A select rather than a checkbox, because the column stores
                  THREE states and a checkbox can only express two. The middle
                  one is the important one:

                    Follow the tier   the default, and what every tenant has
                                      until somebody decides otherwise.
                    Always included   a comp, or a paid add-on for Starter.
                    Never included    withheld from a tier that would have it.

                  A two-state control would have to map "unticked" onto one of
                  "follow the tier" or "never", and either choice is wrong for
                  somebody: the first makes it impossible to withhold, the
                  second turns the calendar off for every Pro and Enterprise
                  tenant the first time this form is saved.

                  The resolved verdict is shown beneath, because the override
                  alone does not say what the tenant actually gets today.
                */}
                {!isNew && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="calendarIncludedOverride">Auction Calendar</label>
                      <select
                        id="calendarIncludedOverride"
                        value={
                          tenant.calendarIncludedOverride === true ? 'true'
                            : tenant.calendarIncludedOverride === false ? 'false'
                              : 'tier'
                        }
                        onChange={(e) => setTenant({
                          ...tenant,
                          calendarIncludedOverride: e.target.value === 'tier'
                            ? null
                            : e.target.value === 'true'
                        })}
                      >
                        <option value="tier">Follow the tier</option>
                        <option value="true">Always included (comp or add-on)</option>
                        <option value="false">Never included</option>
                      </select>
                      <small style={{ color: 'var(--color-text-light)' }}>
                        {tenant.calendarIncludedOverride === null
                          || tenant.calendarIncludedOverride === undefined
                          ? (tenant.calendarIncludedByTier
                            ? 'This tier includes the calendar, so this tenant has it.'
                            : 'This tier does not include the calendar, so this tenant does not have it.')
                          : (tenant.calendarIncludedOverride
                            ? 'Granted by override, whatever the tier says.'
                            : 'Withheld by override, whatever the tier says.')}
                      </small>
                    </div>
                  </div>
                )}

                {!isNew && tenant.trialEndsAt && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Trial Ends</label>
                      <p>{formatDate(tenant.trialEndsAt)}</p>
                    </div>
                    <div className="form-group">
                      <label>Subscription Started</label>
                      <p>{formatDate(tenant.subscriptionStartedAt)}</p>
                    </div>
                  </div>
                )}
              </div>

              {!isNew && (
                <div className="admin-card">
                  <h3>Usage Stats</h3>
                  <div className="tier-breakdown">
                    <div className="tier-item">
                      <span className="tier-label">Active Loans</span>
                      <span className="tier-value">{tenant.activeLoans || 0}</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-label">Properties</span>
                      <span className="tier-value">{tenant.totalProperties || 0}</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-label">Admin Users</span>
                      <span className="tier-value">{tenant.adminUsers || 1}</span>
                    </div>
                  </div>

                  <div style={{ marginTop: '1rem', color: 'var(--color-text-light)', fontSize: '0.9em' }}>
                    <div>Created: {formatDate(tenant.createdAt)}</div>
                    <div>Last Updated: {formatDate(tenant.updatedAt)}</div>
                  </div>
                </div>
              )}

              {/* Billing Management - only for existing tenants */}
              {!isNew && (
                <div className="admin-card" style={{
                  borderLeft: tenant.subscriptionStatus === 'suspended' ? '4px solid #dc2626' :
                              tenant.billingStatus === 'past_due' ? '4px solid #f59e0b' : '4px solid #22c55e'
                }}>
                  <h3>Billing Management</h3>

                  <div className="tier-breakdown" style={{ marginBottom: '1rem' }}>
                    <div className="tier-item">
                      <span className="tier-label">Billing Status</span>
                      <span className="tier-value" style={{
                        color: tenant.billingStatus === 'current' ? '#22c55e' :
                               tenant.billingStatus === 'past_due' ? '#f59e0b' : '#dc2626'
                      }}>
                        {tenant.billingStatus || 'N/A'}
                      </span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-label">Monthly Rate</span>
                      <span className="tier-value">${tenant.monthlyRate || 0}</span>
                    </div>
                    <div className="tier-item">
                      <span className="tier-label">Next Payment</span>
                      <span className="tier-value">{tenant.nextPaymentDate ? formatDate(tenant.nextPaymentDate) : 'N/A'}</span>
                    </div>
                  </div>

                  {/* Late Fee Status */}
                  <div style={{
                    background: tenant.lateFeeApplied ? '#fef2f2' : '#f8fafc',
                    padding: '12px 15px',
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>Late Fee:</strong>{' '}
                        {tenant.lateFeeApplied ? (
                          <span style={{ color: tenant.lateFeeWaived ? '#22c55e' : '#dc2626' }}>
                            ${tenant.lateFeeAmount || 10} {tenant.lateFeeWaived ? '(WAIVED)' : '(APPLIED)'}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-light)' }}>Not applied</span>
                        )}
                      </div>
                      {tenant.lateFeeApplied && (
                        <button
                          type="button"
                          onClick={async () => {
                            const endpoint = tenant.lateFeeWaived ? 'unwaive-late-fee' : 'waive-late-fee';
                            try {
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants/${id}/${endpoint}`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (response.ok) {
                                setSuccess(`Late fee ${tenant.lateFeeWaived ? 'unwaived' : 'waived'} successfully`);
                                fetchTenant();
                              }
                            } catch (err) {
                              setError('Failed to update late fee');
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '0.85em',
                            background: tenant.lateFeeWaived ? '#f59e0b' : '#22c55e',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {tenant.lateFeeWaived ? 'Unwaive Fee' : 'Waive Fee'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Billing Exempt Status */}
                  <div style={{
                    background: tenant.billingExempt ? '#eff6ff' : '#f8fafc',
                    padding: '12px 15px',
                    borderRadius: '8px',
                    marginBottom: '15px',
                    border: tenant.billingExempt ? '2px solid #3b82f6' : '1px solid #e2e8f0'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>Billing Exempt:</strong>{' '}
                        {tenant.billingExempt ? (
                          <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                            YES - Never charged, never expires
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-light)' }}>No - Standard billing</span>
                        )}
                        {tenant.billingExemptReason && (
                          <div style={{ fontSize: '0.85em', color: 'var(--color-text-light)', marginTop: '4px' }}>
                            Reason: {tenant.billingExemptReason}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const newExempt = !tenant.billingExempt;
                          let reason = null;

                          if (newExempt) {
                            reason = window.prompt(
                              'Enter reason for billing exemption:\n(e.g., "Owner account", "Partner account", "Test account")',
                              tenant.billingExemptReason || ''
                            );
                            if (reason === null) return; // User cancelled
                          } else {
                            if (!window.confirm('Remove billing exempt status? This account will be subject to normal billing.')) return;
                          }

                          try {
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants/${id}/billing-exempt`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({ exempt: newExempt, reason })
                            });
                            if (response.ok) {
                              setSuccess(newExempt ? 'Account marked as billing exempt' : 'Billing exempt status removed');
                              fetchTenant();
                            } else {
                              const data = await response.json();
                              setError(data.error || 'Failed to update billing exempt status');
                            }
                          } catch (err) {
                            setError('Failed to update billing exempt status');
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.85em',
                          background: tenant.billingExempt ? '#f59e0b' : '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {tenant.billingExempt ? 'Remove Exemption' : 'Make Exempt'}
                      </button>
                    </div>
                    {!tenant.billingExempt && (
                      <div style={{ fontSize: '0.8em', color: 'var(--color-text-light)', marginTop: '8px' }}>
                        Use for: Owner's accounts, partner accounts, permanent free accounts
                      </div>
                    )}
                  </div>

                  {/* Suspension Status & Controls */}
                  <div style={{
                    background: tenant.subscriptionStatus === 'suspended' ? '#fef2f2' : '#f0fdf4',
                    padding: '12px 15px',
                    borderRadius: '8px',
                    marginBottom: '15px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>Account Status:</strong>{' '}
                        {tenant.subscriptionStatus === 'suspended' ? (
                          <span style={{ color: 'var(--color-error)', fontWeight: '600' }}>
                            SUSPENDED {tenant.suspendedAt && `(since ${formatDate(tenant.suspendedAt)})`}
                          </span>
                        ) : (
                          <span style={{ color: '#22c55e' }}>Active</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const action = tenant.subscriptionStatus === 'suspended' ? 'unsuspend' : 'suspend';
                          const confirmMsg = tenant.subscriptionStatus === 'suspended'
                            ? 'Are you sure you want to reactivate this account?'
                            : 'Are you sure you want to suspend this account? The tenant will lose access.';

                          if (!window.confirm(confirmMsg)) return;

                          try {
                            const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants/${id}/${action}`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({
                                reason: 'Manual action by master admin',
                                resetBilling: action === 'unsuspend'
                              })
                            });
                            if (response.ok) {
                              setSuccess(`Account ${action === 'suspend' ? 'suspended' : 'reactivated'} successfully`);
                              fetchTenant();
                            }
                          } catch (err) {
                            setError(`Failed to ${action} account`);
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: '0.85em',
                          background: tenant.subscriptionStatus === 'suspended' ? '#22c55e' : '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {tenant.subscriptionStatus === 'suspended' ? 'Reactivate Account' : 'Suspend Account'}
                      </button>
                    </div>
                  </div>

                  {/* Trial Management - only show for free trial accounts */}
                  {tenant.subscriptionTier === 'free_trial' && (
                    <div style={{
                      background: (() => {
                        if (!tenant.trialEndsAt) return '#f8fafc';
                        const daysLeft = Math.ceil((new Date(tenant.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24));
                        if (daysLeft <= 0) return '#fef2f2'; // Expired - red
                        if (daysLeft <= 3) return '#fff7ed'; // Expiring soon - orange
                        return '#f0fdf4'; // Active - green
                      })(),
                      padding: '12px 15px',
                      borderRadius: '8px',
                      marginBottom: '15px',
                      border: (() => {
                        if (!tenant.trialEndsAt) return '1px solid #e2e8f0';
                        const daysLeft = Math.ceil((new Date(tenant.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24));
                        if (daysLeft <= 0) return '2px solid #dc2626';
                        if (daysLeft <= 3) return '2px solid #f59e0b';
                        return '1px solid #22c55e';
                      })()
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                          <strong>Trial Status:</strong>{' '}
                          {(() => {
                            if (!tenant.trialEndsAt) return <span style={{ color: 'var(--color-text-light)' }}>No trial date set</span>;
                            const daysLeft = Math.ceil((new Date(tenant.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24));
                            if (daysLeft <= 0) {
                              return (
                                <span style={{ color: 'var(--color-error)', fontWeight: '600' }}>
                                  EXPIRED ({Math.abs(daysLeft)} days ago)
                                  {tenant.suspensionReason === 'trial_expired' && ' - Account Suspended'}
                                </span>
                              );
                            }
                            if (daysLeft <= 3) {
                              return <span style={{ color: '#f59e0b', fontWeight: '600' }}>Expiring Soon ({daysLeft} days left)</span>;
                            }
                            return <span style={{ color: '#22c55e' }}>Active ({daysLeft} days remaining)</span>;
                          })()}
                          <div style={{ fontSize: '0.85em', color: 'var(--color-text-light)', marginTop: '4px' }}>
                            Ends: {tenant.trialEndsAt ? formatDate(tenant.trialEndsAt) : 'Not set'}
                          </div>
                          {tenant.billingExempt && (
                            <div style={{ fontSize: '0.8em', color: '#3b82f6', marginTop: '4px' }}>
                              Note: This account is billing exempt (trial never enforced)
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={async () => {
                              const days = window.prompt(
                                'Extend trial by how many days? (1-90)',
                                '14'
                              );
                              if (!days || isNaN(days) || parseInt(days) < 1 || parseInt(days) > 90) {
                                if (days !== null) setError('Please enter a number between 1 and 90');
                                return;
                              }

                              try {
                                const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants/${id}/extend-trial`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: JSON.stringify({ days: parseInt(days) })
                                });
                                if (response.ok) {
                                  const data = await response.json();
                                  setSuccess(data.message);
                                  fetchTenant();
                                } else {
                                  const data = await response.json();
                                  setError(data.error || 'Failed to extend trial');
                                }
                              } catch (err) {
                                setError('Failed to extend trial');
                              }
                            }}
                            style={{
                              padding: '6px 12px',
                              fontSize: '0.85em',
                              background: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Extend Trial
                          </button>
                          {(() => {
                            const daysLeft = tenant.trialEndsAt ? Math.ceil((new Date(tenant.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
                            if (daysLeft <= 0 && tenant.subscriptionStatus === 'suspended' && tenant.suspensionReason === 'trial_expired') {
                              return (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!window.confirm('Reactivate this trial account with 14 additional days?')) return;
                                    try {
                                      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/tenants/${id}/extend-trial`, {
                                        method: 'POST',
                                        headers: {
                                          'Content-Type': 'application/json',
                                          'Authorization': `Bearer ${token}`
                                        },
                                        body: JSON.stringify({ days: 14 })
                                      });
                                      if (response.ok) {
                                        const data = await response.json();
                                        setSuccess(data.message);
                                        fetchTenant();
                                      }
                                    } catch (err) {
                                      setError('Failed to reactivate trial');
                                    }
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    fontSize: '0.85em',
                                    background: '#22c55e',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Reactivate (+14 days)
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ fontSize: '0.85em', color: 'var(--color-text-light)' }}>
                    <div>Last Payment: {tenant.lastPaymentDate ? formatDate(tenant.lastPaymentDate) : 'Never'}</div>
                    <div>Auto-billing: {tenant.autoBilling ? 'Enabled' : 'Disabled'}</div>
                  </div>
                </div>
              )}

              {/* Payment history. Refunds are issued from here so a refund and
                  the tenant's subscription state are decided in one place. */}
              {!isNew && (
                <div className="admin-card" style={{ marginTop: '1.5rem' }}>
                  <h3>Payment History</h3>

                  {payments.length === 0 ? (
                    <div style={{ color: 'var(--color-text-light)', fontSize: '0.9em', padding: '8px 0' }}>
                      No payments recorded for this tenant.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88em' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '2px solid var(--color-border)' }}>Date</th>
                            <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: '2px solid var(--color-border)' }}>Type</th>
                            <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '2px solid var(--color-border)' }}>Amount</th>
                            <th style={{ textAlign: 'right', padding: '8px 6px', borderBottom: '2px solid var(--color-border)' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map(payment => (
                            <tr key={payment.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                              <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                                {formatDate(payment.paymentDate)}
                              </td>
                              <td style={{ padding: '8px 6px', textTransform: 'capitalize' }}>
                                {(payment.paymentType || '').replace('_', ' ')}
                                {payment.isRefund && payment.refundReason && (
                                  <div style={{ fontSize: '0.8em', color: 'var(--color-text-light)' }}>
                                    {payment.refundReason}
                                  </div>
                                )}
                              </td>
                              <td style={{
                                padding: '8px 6px',
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                                color: payment.isRefund ? '#dc3545' : 'inherit'
                              }}>
                                {/* Refund amounts are stored negative; the sign
                                    is already in the value. */}
                                ${Math.abs(payment.amount).toFixed(2)}
                                {payment.isRefund && ' refunded'}
                                {!payment.isRefund && payment.refundedAmount > 0 && (
                                  <div style={{ fontSize: '0.8em', color: 'var(--color-error)' }}>
                                    ${payment.refundedAmount.toFixed(2)} refunded
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                                {payment.refundable && (
                                  <button
                                    type="button"
                                    onClick={() => openRefund(payment)}
                                    style={{
                                      padding: '5px 10px',
                                      fontSize: '0.85em',
                                      background: '#dc3545',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    Refund
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving || (isNew && subdomainStatus.available === false)}>
                  {saving ? 'Saving...' : (isNew ? 'Create Tenant' : 'Save Changes')}
                </button>

                {!isNew && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDelete}
                    style={{ marginLeft: '1rem' }}
                  >
                    Delete Tenant
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </main>

      {refundTarget && refundPreview && (
        <RefundModal
          isOpen
          onClose={() => { setRefundTarget(null); setRefundPreview(null); }}
          onSubmit={submitRefund}
          title={`Refund ${refundPreview.payment.companyName}`}
          maxRefundable={refundPreview.maxRefundable}
          alreadyRefunded={refundPreview.alreadyRefunded}
          methodLabel={
            refundPreview.canRefundViaSquare
              ? 'Issues a real refund through Square, and records it here.'
              : 'No Square payment to reverse. This records the refund on the books only.'
          }
          lines={[
            { label: 'Payment', value: `#${refundPreview.payment.id}` },
            { label: 'Date', value: formatDate(refundPreview.payment.paymentDate) },
            { label: 'Type', value: (refundPreview.payment.paymentType || '').replace('_', ' ') },
            { label: 'Original amount', value: `$${refundPreview.payment.amount.toFixed(2)}`, strong: true }
          ]}
          renderEffect={(amount) => (
            <>
              <div><strong>${amount.toFixed(2)}</strong> will be returned to {refundPreview.payment.companyName}.</div>
              <div style={{ marginTop: '4px' }}>
                Revenue This Month and Revenue All Time both drop by ${amount.toFixed(2)}.
              </div>
              <div style={{ marginTop: '4px', color: 'var(--color-text-light)' }}>
                MRR is unchanged. It tracks the subscribed rate, not cash collected.
                {cancelTenant && ' Cancelling below is what removes them from MRR.'}
              </div>
            </>
          )}
          extraFields={
            <div style={{
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              padding: '10px 12px',
              marginBottom: '12px'
            }}>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={cancelTenant}
                  onChange={(e) => setCancelTenant(e.target.checked)}
                  style={{ marginTop: '3px' }}
                />
                <span>
                  Also set this tenant to <strong>Cancelled</strong>
                  <div style={{ color: 'var(--color-text-light)', marginTop: '2px' }}>
                    Current status: {refundPreview.tenant.subscriptionStatus}
                    {refundPreview.tenant.hasCardOnFile ? ' · card on file' : ' · no card on file'}.
                    {refundPreview.tenant.hasCardOnFile && (
                      cancelTenant
                        ? ' Recurring billing will be stopped, so the card will not be charged again.'
                        : ' Leaving them active means recurring billing will charge that card again on the next cycle.'
                    )}
                  </div>
                </span>
              </label>
            </div>
          }
          warning={
            refundPreview.canRefundViaSquare
              ? 'This sends a real refund to Square. It cannot be undone from here.'
              : 'This payment did not go through Square. Recording a refund here corrects the books only. No money moves.'
          }
        />
      )}
    </div>
  );
}

export default MasterAdminTenantDetail;
